/**
 * DOCUMENTATION:
 * Component: AIProjectRAG (Project Page)
 * Purpose: The Capstone project description for the AI & LLM track.
 * * * FOR JUNIOR DEVELOPERS:
 * 1. Stepper UI: Visualizes the 4 stages of a RAG pipeline.
 * 2. Component Logic: Explains how the knowledgeBase.js maps to Gemini.
 * 3. Best Practices: Reinforces the "Free Tier" architecture (Local Search).
 */

import React from "react";
import {
    Box, Typography, Card, Grid, Stack, Stepper, Step,
    StepLabel, Button, styled, Paper, Divider
} from "@mui/material";
import { Link } from "react-router-dom";
import TerminalIcon from '@mui/icons-material/Terminal';
import StorageIcon from '@mui/icons-material/Storage';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ConstructionIcon from '@mui/icons-material/Construction';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a0519",
    padding: theme.spacing(12, 2, 8),
    color: "white",
}));

const ProjectCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(30, 20, 50, 0.8)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    border: "1px solid rgba(167, 139, 250, 0.2)",
    color: "white",
}));

export default function AIProjectRAG() {
    const steps = [
        "Data Ingestion",
        "Vector Embedding",
        "Similarity Search",
        "Augmented Generation"
    ];

    const stepDetails = [
        {
            title: "1. Data Ingestion",
            desc: "Collecting hard-coded lesson data from 'knowledgeBase.js' and preparing it for indexing.",
            icon: <StorageIcon color="primary" />
        },
        {
            title: "2. Vector Embedding",
            desc: "Using Gemini's 'embedding-001' model to convert text into multi-dimensional numerical vectors.",
            icon: <TerminalIcon color="secondary" />
        },
        {
            title: "3. Similarity Search",
            desc: "Calculating the cosine distance between the user's question and the indexed lesson snippets.",
            icon: <ConstructionIcon sx={{ color: "#34A853" }} />
        },
        {
            title: "4. Augmented Generation",
            desc: "Sending the question + found context to Gemini 1.5 Flash to generate a grounded answer.",
            icon: <PsychologyIcon sx={{ color: "#FBBC04" }} />
        }
    ];

    return (
        <PageWrapper>
            <Stack spacing={4} sx={{ maxWidth: "1000px", margin: "0 auto" }}>
                {/* Header Section */}
                <Box textAlign="center">
                    <Typography variant="h3" fontWeight={900} color="#a78bfa">
                        PROJECT: THE RAG PIPELINE
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.7 }}>
                        Building a Context-Aware AI Mentor for ProDev
                    </Typography>
                </Box>

                <ProjectCard>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        Architecture Overview
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.8, mb: 4, lineHeight: 1.8 }}>
                        In this final project, you integrate <strong>LangChain</strong> and <strong>Gemini</strong>
                        to build a Retrieval-Augmented Generation (RAG) system. Unlike basic chatbots, this
                        system only answers questions based on the specific documentation provided in your app.
                    </Typography>



                    <Stepper activeStep={4} alternativeLabel sx={{ mb: 4 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel sx={{ '& .MuiStepLabel-label': { color: 'white', opacity: 0.7 } }}>
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Grid container spacing={3}>
                        {stepDetails.map((step, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                                <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.03)", color: "white", height: '100%' }}>
                                    <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                                        {step.icon}
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {step.title}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                                        {step.desc}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </ProjectCard>

                {/* Final Goal */}
                <Box textAlign="center" sx={{ py: 4 }}>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                        Ready to Test the Pipeline?
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.7, mb: 3 }}>
                        Use the "AI Mentor" floating button on your dashboard to see this architecture in action.
                    </Typography>
                    <Link to="/ai-quiz" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            sx={{ bgcolor: "#a78bfa", color: "#0a0519", fontWeight: 800, px: 6 }}
                        >
                            TAKE CERTIFICATION QUIZ
                        </Button>
                    </Link>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.1)" }} />

                <Typography variant="body2" sx={{ textAlign: "center", opacity: 0.5 }}>
                    Final Project • AI & LLM Integration Track • ProDev Platform 2026
                </Typography>
            </Stack>
        </PageWrapper>
    );
}
